import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  XMarkIcon, 
  ShareIcon, 
  UserPlusIcon,
  TrashIcon,
  CheckCircleIcon,
  ClockIcon,
  EyeIcon,
  MapPinIcon,
  PhotoIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import toast from 'react-hot-toast';

const theme = {
  colors: {
    background: {
      primary: '#0f172a',
      secondary: '#1e293b',
      tertiary: '#334155',
      card: '#1e293b'
    },
    text: {
      primary: '#f8fafc',
      secondary: '#cbd5e1',
      disabled: '#64748b'
    },
    primary: {
      main: '#3b82f6'
    },
    border: {
      primary: '#334155'
    }
  },
  borderRadius: {
    sm: '0.375rem',
    md: '0.5rem',
    lg: '0.75rem',
    full: '9999px'
  }
};

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  backdrop-filter: blur(4px);
`;

const Modal = styled(motion.div)`
  background: ${theme.colors.background.card};
  border-radius: ${theme.borderRadius.lg};
  max-width: 700px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5);
  border: 1px solid ${theme.colors.border.primary};
  overflow: hidden;
`;

const Header = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid ${theme.colors.border.primary};
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${theme.colors.background.card};
  flex-shrink: 0;
`;

const Title = styled.h2`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0;
  font-size: 1.5rem;
  color: ${theme.colors.text.primary};
  
  svg {
    width: 1.75rem;
    height: 1.75rem;
    color: ${theme.colors.primary.main};
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  padding: 0.5rem;
  border-radius: ${theme.borderRadius.md};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${theme.colors.background.tertiary};
    color: ${theme.colors.text.primary};
  }
  
  svg {
    width: 1.5rem;
    height: 1.5rem;
  }
`;

const Content = styled.div`
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

const Section = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.125rem;
  color: ${theme.colors.text.primary};
  margin: 0 0 1rem 0;
  font-weight: 600;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  color: ${theme.colors.text.secondary};
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  background: ${theme.colors.background.tertiary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.text.primary};
  font-size: 0.9375rem;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    background: ${theme.colors.background.card};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &::placeholder {
    color: ${theme.colors.text.disabled};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  background: ${theme.colors.background.tertiary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.text.primary};
  font-size: 0.9375rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    background: ${theme.colors.background.card};
  }
  
  option {
    background: ${theme.colors.background.tertiary};
    color: ${theme.colors.text.primary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  background: ${theme.colors.background.tertiary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.text.primary};
  font-size: 0.9375rem;
  font-family: inherit;
  resize: vertical;
  min-height: 80px;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    background: ${theme.colors.background.card};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &::placeholder {
    color: ${theme.colors.text.disabled};
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: ${theme.colors.text.primary};
  font-size: 0.9375rem;
  cursor: pointer;
  padding: 0.75rem;
  background: ${theme.colors.background.tertiary};
  border-radius: ${theme.borderRadius.md};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${theme.colors.background.primary};
  }
  
  input {
    width: 1.25rem;
    height: 1.25rem;
    cursor: pointer;
    accent-color: ${theme.colors.primary.main};
  }
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: ${theme.colors.primary.main};
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  background: ${props => props.variant === 'primary' ? theme.colors.primary.main : theme.colors.background.tertiary};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: ${props => props.fullWidth ? '100%' : 'auto'};
  
  &:hover {
    background: ${props => props.variant === 'primary' ? '#2563eb' : theme.colors.background.primary};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const SharesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ShareItem = styled.div`
  background: ${theme.colors.background.tertiary};
  border-radius: ${theme.borderRadius.md};
  padding: 1rem;
  border: 1px solid ${theme.colors.border.primary};
`;

const ShareHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const ShareName = styled.div`
  font-weight: 600;
  color: ${theme.colors.text.primary};
  font-size: 1rem;
`;

const ShareEmail = styled.div`
  color: ${theme.colors.text.secondary};
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
`;

const ShareMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: ${theme.colors.text.secondary};
`;

const Badge = styled.span`
  padding: 0.25rem 0.75rem;
  background: ${props => props.active ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)'};
  color: ${props => props.active ? '#4ade80' : '#f87171'};
  border-radius: ${theme.borderRadius.full};
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const AccessBadge = styled.span`
  padding: 0.25rem 0.75rem;
  background: rgba(59, 130, 246, 0.2);
  color: #60a5fa;
  border-radius: ${theme.borderRadius.full};
  font-size: 0.75rem;
  font-weight: 600;
`;

const ShareActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const IconButton = styled.button`
  padding: 0.5rem;
  background: ${theme.colors.background.primary};
  border: none;
  border-radius: ${theme.borderRadius.md};
  color: ${props => props.danger ? '#f87171' : theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.danger ? 'rgba(239, 68, 68, 0.2)' : theme.colors.background.secondary};
    color: ${props => props.danger ? '#ef4444' : theme.colors.text.primary};
  }
  
  svg {
    width: 1.125rem;
    height: 1.125rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1.5rem;
  color: ${theme.colors.text.secondary};
  
  svg {
    width: 4rem;
    height: 4rem;
    margin: 0 auto 1rem;
    opacity: 0.5;
  }
  
  h4 {
    color: ${theme.colors.text.primary};
    margin: 0 0 0.5rem 0;
    font-size: 1.125rem;
  }
  
  p {
    margin: 0;
    font-size: 0.9375rem;
  }
`;

const ShareTripModal = ({ tripId, tripName, isOpen, onClose }) => {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(null); // { shareId, email }
  
  // Form state
  const [formData, setFormData] = useState({
    shared_with_email: '',
    shared_with_name: '',
    access_level: 'view_only',
    can_view_photos: true,
    can_view_location: true,
    can_view_statistics: true,
    expires_at: '',
    share_message: ''
  });

  useEffect(() => {
    if (isOpen && tripId) {
      loadShares();
    }
  }, [isOpen, tripId]);

  const loadShares = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/trip-sharing/${tripId}/shares`);
      setShares(response.data.shares || []);
    } catch (error) {
      console.error('Error loading shares:', error);
      toast.error('Failed to load trip shares');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async (e) => {
    e.preventDefault();
    
    if (!formData.shared_with_email) {
      toast.error('Email address is required');
      return;
    }

    try {
      setLoading(true);
      const response = await api.post(`/api/trip-sharing/${tripId}/shares`, formData);
      
      toast.success(`Trip shared with ${formData.shared_with_email}!`);
      
      // Copy share URL to clipboard
      if (response.data.shareUrl) {
        try {
          if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(response.data.shareUrl);
            toast.success('Share link copied to clipboard!');
          } else {
            // Fallback: just show success without clipboard
            console.log('Share URL:', response.data.shareUrl);
          }
        } catch (clipboardError) {
          console.error('Clipboard error:', clipboardError);
          // Continue without clipboard - share was still created successfully
        }
      }
      
      // Reset form and reload shares
      setFormData({
        shared_with_email: '',
        shared_with_name: '',
        access_level: 'view_only',
        can_view_photos: true,
        can_view_location: true,
        can_view_statistics: true,
        expires_at: '',
        share_message: ''
      });
      setShowAddForm(false);
      loadShares();
      
    } catch (error) {
      console.error('Error sharing trip:', error);
      const errorMessage = error.response?.data?.error || 'Failed to share trip';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = (shareId, email) => {
    setShowRevokeConfirm({ shareId, email });
  };

  const confirmRevoke = async () => {
    if (!showRevokeConfirm) return;

    try {
      await api.delete(`/api/trip-sharing/${tripId}/shares/${showRevokeConfirm.shareId}`);
      toast.success('Share revoked');
      loadShares();
    } catch (error) {
      console.error('Error revoking share:', error);
      toast.error('Failed to revoke share');
    } finally {
      setShowRevokeConfirm(null);
    }
  };

  const handleCopyLink = async (shareToken) => {
    const shareUrl = `${window.location.origin}/shared-trip/${shareToken}`;
    
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Share link copied to clipboard!');
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        toast.success('Share link copied to clipboard!');
      }
    } catch (error) {
      console.error('Clipboard error:', error);
      toast.error('Could not copy link. Please copy manually from the share list.');
    }
  };

  if (!isOpen) return null;

  return (
    <Overlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <Modal
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <Header>
          <Title>
            <ShareIcon />
            Share Trip
          </Title>
          <CloseButton onClick={onClose}>
            <XMarkIcon />
          </CloseButton>
        </Header>

        <Content>
          {/* Add New Share Form */}
          {showAddForm ? (
            <Section>
              <SectionTitle>Share "{tripName}"</SectionTitle>
              
              <form onSubmit={handleShare}>
                <FormGroup>
                  <Label>Email Address *</Label>
                  <Input
                    type="email"
                    placeholder="friend@example.com"
                    value={formData.shared_with_email}
                    onChange={(e) => setFormData({ ...formData, shared_with_email: e.target.value })}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Name (Optional)</Label>
                  <Input
                    type="text"
                    placeholder="John Doe"
                    value={formData.shared_with_name}
                    onChange={(e) => setFormData({ ...formData, shared_with_name: e.target.value })}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Access Level</Label>
                  <Select
                    value={formData.access_level}
                    onChange={(e) => setFormData({ ...formData, access_level: e.target.value })}
                  >
                    <option value="view_only">View Only - Basic trip info</option>
                    <option value="progress_updates">Progress Updates - Real-time location</option>
                    <option value="full_access">Full Access - Everything</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>What they can view:</Label>
                  <CheckboxGroup>
                    <CheckboxLabel>
                      <input
                        type="checkbox"
                        checked={formData.can_view_photos}
                        onChange={(e) => setFormData({ ...formData, can_view_photos: e.target.checked })}
                      />
                      <PhotoIcon />
                      <span>Photos</span>
                    </CheckboxLabel>
                    
                    <CheckboxLabel>
                      <input
                        type="checkbox"
                        checked={formData.can_view_location}
                        onChange={(e) => setFormData({ ...formData, can_view_location: e.target.checked })}
                      />
                      <MapPinIcon />
                      <span>Location & GPS Track</span>
                    </CheckboxLabel>
                    
                    <CheckboxLabel>
                      <input
                        type="checkbox"
                        checked={formData.can_view_statistics}
                        onChange={(e) => setFormData({ ...formData, can_view_statistics: e.target.checked })}
                      />
                      <ChartBarIcon />
                      <span>Statistics (Distance, Speed, Fuel)</span>
                    </CheckboxLabel>
                  </CheckboxGroup>
                </FormGroup>

                <FormGroup>
                  <Label>Expires On (Optional)</Label>
                  <Input
                    type="datetime-local"
                    value={formData.expires_at}
                    onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Personal Message (Optional)</Label>
                  <TextArea
                    placeholder="Hey! Check out my trip. Hope you enjoy following along!"
                    value={formData.share_message}
                    onChange={(e) => setFormData({ ...formData, share_message: e.target.value })}
                  />
                </FormGroup>

                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <Button type="submit" variant="primary" disabled={loading} style={{ flex: 1 }}>
                    <ShareIcon />
                    Share Trip
                  </Button>
                  <Button type="button" onClick={() => setShowAddForm(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </Section>
          ) : (
            <>
              {/* Existing Shares */}
              <Section>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <SectionTitle style={{ margin: 0 }}>Shared With</SectionTitle>
                  <Button variant="primary" onClick={() => setShowAddForm(true)}>
                    <UserPlusIcon />
                    Share Trip
                  </Button>
                </div>

                {loading ? (
                  <EmptyState>
                    <p>Loading shares...</p>
                  </EmptyState>
                ) : shares.length === 0 ? (
                  <EmptyState>
                    <ShareIcon />
                    <h4>No Shares Yet</h4>
                    <p>Share this trip with friends and family so they can follow along!</p>
                  </EmptyState>
                ) : (
                  <SharesList>
                    {shares.map((share) => (
                      <ShareItem key={share.id}>
                        <ShareHeader>
                          <ShareName>{share.shared_with_name || share.shared_with_email}</ShareName>
                          <ShareActions>
                            <IconButton
                              onClick={() => handleCopyLink(share.share_token)}
                              title="Copy share link"
                            >
                              <ShareIcon />
                            </IconButton>
                            <IconButton
                              danger
                              onClick={() => handleRevoke(share.id, share.shared_with_email)}
                              title="Revoke access"
                            >
                              <TrashIcon />
                            </IconButton>
                          </ShareActions>
                        </ShareHeader>
                        
                        <ShareEmail>{share.shared_with_email}</ShareEmail>
                        
                        <ShareMeta>
                          <Badge active={share.is_active}>
                            {share.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                          <AccessBadge>{share.access_level.replace('_', ' ')}</AccessBadge>
                          {share.accepted_at && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                              <CheckCircleIcon style={{ width: '1rem', height: '1rem' }} />
                              Viewed
                            </span>
                          )}
                          {share.expires_at && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                              <ClockIcon style={{ width: '1rem', height: '1rem' }} />
                              Expires {new Date(share.expires_at).toLocaleDateString()}
                            </span>
                          )}
                          {share.access_count > 0 && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                              <EyeIcon style={{ width: '1rem', height: '1rem' }} />
                              {share.access_count} views
                            </span>
                          )}
                        </ShareMeta>
                      </ShareItem>
                    ))}
                  </SharesList>
                )}
              </Section>
            </>
          )}
        </Content>
      </Modal>

      {/* Revoke Confirmation Modal */}
      {showRevokeConfirm && (
        <Overlay 
          style={{ zIndex: 1001 }}
          onClick={() => setShowRevokeConfirm(null)}
        >
          <Modal
            style={{ maxWidth: '500px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <Header>
              <Title style={{ fontSize: '1.25rem' }}>
                Revoke Access
              </Title>
              <CloseButton onClick={() => setShowRevokeConfirm(null)}>
                <XMarkIcon />
              </CloseButton>
            </Header>

            <Content>
              <div style={{ 
                background: 'rgba(239, 68, 68, 0.1)', 
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: theme.borderRadius.md,
                padding: '1rem',
                marginBottom: '1.5rem'
              }}>
                <p style={{ 
                  color: theme.colors.text.primary, 
                  margin: '0 0 0.5rem 0',
                  fontWeight: 600
                }}>
                  Are you sure you want to revoke access?
                </p>
                <p style={{ 
                  color: theme.colors.text.secondary, 
                  margin: 0,
                  fontSize: '0.9375rem'
                }}>
                  <strong>{showRevokeConfirm.email}</strong> will immediately lose access to this trip.
                  Their share link will stop working.
                </p>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <Button onClick={() => setShowRevokeConfirm(null)}>
                  Cancel
                </Button>
                <Button 
                  variant="primary" 
                  onClick={confirmRevoke}
                  style={{ background: '#ef4444' }}
                >
                  <TrashIcon />
                  Revoke Access
                </Button>
              </div>
            </Content>
          </Modal>
        </Overlay>
      )}
    </Overlay>
  );
};

export default ShareTripModal;

